export const API_VERSION_PREFIX = '/api/v1';

const id = encodeURIComponent;

const HUB = `${API_VERSION_PREFIX}/brand_hub`;

/** The four CMYK channels that name one proposed colour, whatever it was labelled. */
export interface CmykShade {
  cyan: number;
  magenta: number;
  yellow: number;
  key: number;
}

const shade = (values: CmykShade) => new URLSearchParams(Object.entries(values).map(([channel, value]) => [channel, String(value)])).toString();

export const endpoints = {
  health: () => `${API_VERSION_PREFIX}/health`,

  auth: {
    session: () => `${API_VERSION_PREFIX}/auth/session`,
    selectOrganization: () => `${API_VERSION_PREFIX}/auth/session/organization`,
    logout: () => `${API_VERSION_PREFIX}/auth/logout`,
    // Admin only. Reports the groups an admin may grant, not what the caller holds.
    permissions: () => `${API_VERSION_PREFIX}/auth/permissions`,
  },

  dashboard: {
    summary: () => `${API_VERSION_PREFIX}/dashboard/summary`,
    attention: () => `${API_VERSION_PREFIX}/dashboard/attention`,
  },

  campaigns: {
    list: () => `${API_VERSION_PREFIX}/campaigns`,
    create: () => `${API_VERSION_PREFIX}/campaigns`,
    detail: (campaignId: string) => `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}`,
    update: (campaignId: string) => `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}`,
    performance: (campaignId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/performance`,
    deactivate: (campaignId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/deactivate`,
  },

  assets: {
    list: (campaignId: string) => `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/assets`,
    create: (campaignId: string) => `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/assets`,
    detail: (campaignId: string, assetId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/assets/${id(assetId)}`,
    generate: (campaignId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/assets/generate`,
    schedule: (campaignId: string, assetId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/assets/${id(assetId)}/schedule`,
    retry: (campaignId: string, assetId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/assets/${id(assetId)}/retry`,
  },

  analytics: {
    kpis: (campaignId: string, assetId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/assets/${id(assetId)}/kpis`,
    trend: (campaignId: string, assetId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/assets/${id(assetId)}/trend`,
  },

  monitoring: {
    anomalies: (campaignId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/anomalies`,
    resolveAnomaly: (campaignId: string, anomalyId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/anomalies/${id(anomalyId)}/resolve`,
    redZones: (campaignId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/red-zones`,
  },

  visualQuality: {
    report: (campaignId: string, assetId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/assets/${id(assetId)}/visual-quality`,
    campaignReports: (campaignId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/visual-quality`,
  },

  // Brands and their assets are addressed by slug and id, products by slug and sku.
  // Every `list` and `imports` is answered by MSW only — the hub streams from Firestore.
  brandHub: {
    brands: () => `${HUB}/brands`,
    brand: {
      create: () => `${HUB}/brands`,
      update: (slug: string) => `${HUB}/brands/${id(slug)}`,
      colours: (slug: string) => `${HUB}/brands/${id(slug)}/colours`,
      extractColours: (slug: string) => `${HUB}/brands/${id(slug)}/colours/extract`,
      extractedColour: (slug: string, values: CmykShade) => `${HUB}/brands/${id(slug)}/colours/extracted?${shade(values)}`,
      fonts: (slug: string) => `${HUB}/brands/${id(slug)}/fonts`,
      font: (slug: string, fontId: string) => `${HUB}/brands/${id(slug)}/fonts/${id(fontId)}`,
      deactivate: (slug: string) => `${HUB}/brands/${id(slug)}`,
      assets: {
        list: (slug: string) => `${HUB}/brands/${id(slug)}/assets`,
        create: (slug: string) => `${HUB}/brands/${id(slug)}/assets`,
        update: (slug: string, assetId: string) =>
          `${HUB}/brands/${id(slug)}/assets/${id(assetId)}`,
        delete: (slug: string, assetId: string) =>
          `${HUB}/brands/${id(slug)}/assets/${id(assetId)}`,
      },
    },
    product: {
      list: (slug: string) => `${HUB}/brands/${id(slug)}/products`,
      create: (slug: string) => `${HUB}/brands/${id(slug)}/products`,
      update: (slug: string, productId: string) =>
        `${HUB}/brands/${id(slug)}/products/${id(productId)}`,
      delete: (slug: string, productId: string) =>
        `${HUB}/brands/${id(slug)}/products/${id(productId)}`,
      restoreVersion: (slug: string, productId: string, versionId: string) =>
        `${HUB}/brands/${id(slug)}/products/${id(productId)}/versions/${id(versionId)}/restore`,
      importCheck: (slug: string) => `${HUB}/brands/${id(slug)}/products/import/check`,
      import: (slug: string) => `${HUB}/brands/${id(slug)}/products/import`,
      imports: (slug: string) => `${HUB}/brands/${id(slug)}/products/imports`,
    },
    templates: () => `${HUB}/templates`,
    template: {
      inspect: () => `${HUB}/templates/inspect`,
      create: () => `${HUB}/templates`,
      update: (templateId: string) => `${HUB}/templates/${id(templateId)}`,
      delete: (templateId: string) => `${HUB}/templates/${id(templateId)}`,
    },
    generatedAssets: () => `${HUB}/generated_assets`,
  },

  organization: {
    detail: () => `${API_VERSION_PREFIX}/organization`,
    update: () => `${API_VERSION_PREFIX}/organization`,
  },

  // `list` is answered by MSW only. In a configured environment the members of an
  // organization stream from Firestore, which is what active_organization_ids is for.
  users: {
    list: () => `${API_VERSION_PREFIX}/users`,
    add: () => `${API_VERSION_PREFIX}/users`,
    update: (email: string) => `${API_VERSION_PREFIX}/users/${id(email)}`,
    revoke: (email: string) => `${API_VERSION_PREFIX}/users/${id(email)}`,
  },

  profile: {
    detail: () => `${API_VERSION_PREFIX}/profile`,
    update: () => `${API_VERSION_PREFIX}/profile`,
    preferences: () => `${API_VERSION_PREFIX}/profile/preferences`,
  },
} as const;
